import { HttpResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { pages } from '@shared/config/pages';
import { Hospital } from '@shared/models/hospital.model';
import { HospitalNames } from '@shared/models/hospitalTypes.model';
import { Task, TaskOperations, TasksPaginatedResponse, TaskStatuses } from '@shared/models/task.model';
import { UserService } from '@shared/services/user.service';
import { formatDuration } from '@shared/utils/cross-functions';
import { saveAs } from 'file-saver';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { ErrorService } from '../../services/error.service';
import { InstanceIdService } from '../../services/instance-id.service';
import { StatopService } from '../../services/statop.service';
import { SubscriptionManager } from '../../services/subscription-manager.service';
import { TaskService } from '../../services/task.service';

@Component({
  selector: 'app-tasks-floating-button',
  templateUrl: './tasks-floating-button.component.html',
  styleUrl: './tasks-floating-button.component.scss',
  standalone: false,
})
export class TasksFloatingButtonComponent implements OnInit {
  @ViewChild('tasksContainer') public tasksContainer?: ElementRef;

  @Input() public elevated: boolean | undefined;

  public isOpen: boolean = false;

  public showHistory: boolean = false;

  public runningTasksCount: number = 0;

  public tasks: Task[] = [];

  public isLoading: boolean = false;

  public isLoadingMore: boolean = false;

  public isEmpty: boolean = false;

  public hasEverHadTasks: boolean = false;

  private currentHospitals: Hospital[] | undefined = [];

  public hospitalMap: Record<string, string> = {};

  public isMultiHospital: boolean = false;

  public readonly taskStatusConfig = {
    succeeded: {
      icon: 'check_circle_outlined',
    },
    failed: {
      icon: 'error_outline',
    },
    running: {
      icon: 'pending',
    },
    queued: {
      icon: 'schedule',
    },
    canceled: {
      icon: 'cancel',
    },
    default: {
      icon: 'help_outline',
    },
  } as const;

  public readonly resultButtonLabels: Record<string, string> = {
    [TaskOperations.REQUEST_REAL_PROGRAMS]: 'Actualiser',
    [TaskOperations.REQUEST_CALENDARS]: 'Actualiser',
    [TaskOperations.REQUEST_DOCTOR_AGENDAS]: 'Actualiser',
    [TaskOperations.REQUEST_SURGEON_OPENINGS]: 'Actualiser',
    [TaskOperations.CYCLE_PLANNING]: 'Afficher',
    [TaskOperations.TVO]: 'Afficher',
    [TaskOperations.ANESTH_NEEDS]: 'Afficher',
    //[TaskOperations.EXPORT]: 'Télécharger',
    [TaskOperations.STATS]: 'Télécharger',
    [TaskOperations.EXPORT_PROFILES_REPORTS]: 'Télécharger',
    [TaskOperations.CORRESPONDANCES]: 'Actualiser',
    [TaskOperations.CAMPAIGN_PLANNING]: 'Afficher',
    [TaskOperations.ADJUSTMENT_PLANNING]: 'Afficher',
  };

  public readonly retryableTasks: string[] = [
    //TaskOperations.REQUEST_CALENDARS,
    TaskOperations.REQUEST_SURGEON_OPENINGS,
    TaskOperations.REQUEST_REAL_PROGRAMS,
    TaskOperations.REQUEST_DOCTOR_AGENDAS,
  ] as const;

  public tasksOperationLabels: Record<string, string> = {};

  public hospitalNames: HospitalNames | undefined;

  private currentPage: number = 1;

  private readonly pageSize: number = 10;

  public hasNextPage: boolean = false;

  public hasPrevPage: boolean = false;

  private readonly instanceId: string;

  private readonly completedTasksHandled = new Set<string>();

  constructor(
    private readonly taskService: TaskService,
    private readonly subscriptionManager: SubscriptionManager,
    private readonly destroyRef: DestroyRef,
    private readonly instanceIdService: InstanceIdService,
    private readonly errorService: ErrorService,
    private readonly navigationService: NavigationService,
    private readonly usersService: UserService,
    private readonly statopService: StatopService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  ngOnInit(): void {
    this.hospitalNames = this.usersService.getHospitalNames();
    this.currentHospitals = this.usersService.usersHospitals;
    this.currentHospitals!.forEach((h: any) => (this.hospitalMap[h._id] = h.name));

    this.initializeOperationsLabels();
    this.resolveSelectedHospitals();

    this.subscriptionManager.createRequest(this.taskService.taskUpdate$, 'taskUpdates', this.instanceId).subscribe({
      next: (updatedTask: Task) => {
        this.handleTaskUpdate(updatedTask);
      },
      error: (error: any) => {
        this.errorService.handleError(error);
      },
    });

    this.subscriptionManager.createRequest(this.taskService.reloadTasks$, 'reloadTasks', this.instanceId).subscribe({
      next: () => {
        this.resolveSelectedHospitals();
        this.currentPage = 1;
        this.tasks = [];
        this.completedTasksHandled.clear();
        this.runningTasksCount = 0;
        if (this.showHistory) {
          this.loadAllTasks();
        } else {
          this.loadRunningTasks();
        }
      },
      error: (error: any) => {
        this.errorService.handleError(error);
      },
    });

    this.loadRunningTasks();
    this.checkHasEverHadTasks();
  }

  private handleTaskUpdate(updatedTask: Task) {
    this.hasEverHadTasks = true;

    const existingTaskIndex = this.tasks.findIndex((task) => task._id === updatedTask._id);
    if (existingTaskIndex === -1) {
      this.tasks.unshift(updatedTask);
      this.isOpen = true;
      this.isEmpty = false;
      if (updatedTask.status === TaskStatuses.RUNNING) {
        this.runningTasksCount++;
      }
      setTimeout(() => this.scrollToTop());

      this.handleTaskCompleted(updatedTask);
    } else {
      const currentTask = this.tasks[existingTaskIndex];

      if (currentTask.status === TaskStatuses.RUNNING && updatedTask.status !== TaskStatuses.RUNNING) {
        this.runningTasksCount = Math.max(0, this.runningTasksCount - 1);
        this.isOpen = true;
      }

      Object.assign(this.tasks[existingTaskIndex], updatedTask);

      const hasJustSucceeded =
        currentTask.status !== TaskStatuses.SUCCEEDED && updatedTask.status === TaskStatuses.SUCCEEDED;

      if (hasJustSucceeded) {
        this.handleTaskCompleted(updatedTask);
      }
    }
  }

  private handleTaskCompleted(task: Task): void {
    if (task.status !== TaskStatuses.SUCCEEDED) {
      return;
    }

    if (!task._id || this.completedTasksHandled.has(task._id)) {
      return;
    }
    this.completedTasksHandled.add(task._id);

    try {
      switch (task.operation) {
        case TaskOperations.STATS:
          if (task.data.excelExport) {
            this.handleStatTask(task);
          }
          break;

        default:
          break;
      }
    } catch (error: any) {
      this.errorService.handleError(error);
    }
  }

  private checkHasEverHadTasks(): void {
    this.subscriptionManager
      .createRequest(this.taskService.getAllTasks(1, 1), 'checkHasEverHadTasks', this.instanceId)
      .subscribe({
        next: (response: TasksPaginatedResponse) => {
          if (response.totalDocs > 0) {
            this.hasEverHadTasks = true;
          }
        },
        error: (error: any) => this.errorService.handleError(error),
      });
  }

  initializeOperationsLabels() {
    this.tasksOperationLabels = {
      REQUEST_REAL_PROGRAMS: 'Interventions réalisées',
      REQUEST_CALENDARS: 'Calendriers',
      REQUEST_DOCTOR_AGENDAS: 'Interventions planifiées',
      REQUEST_SURGEON_OPENINGS: 'Vacation',
      CYCLE_PLANNING: (this.hospitalNames?.cyclesName || 'Roulement') + 's',
      TVO: this.hospitalNames?.vacationsName || 'TVO',
      ANESTH_NEEDS: "Besoins d'anesthésie",
      EXPORT: 'Export',
      STATS: 'Statistiques',
      EXPORT_PROFILES_REPORTS: 'Rapports de compétences',
      CAMPAIGN_PLANNING: 'Génération de campagne',
      ADJUSTMENT_PLANNING: 'Ajustement de planning',
    };
  }

  private resolveSelectedHospitals(): void {
    const selectedHospitals = this.usersService.getSelectedHospitals();
    this.isMultiHospital = selectedHospitals.length > 1;
  }

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
  }

  minimize(): void {
    this.isOpen = false;
  }

  showHistoryView(): void {
    this.showHistory = true;
    this.currentPage = 1;
    this.tasks = [];
    this.loadAllTasks();
  }

  showRunningView(): void {
    this.showHistory = false;
    this.currentPage = 1;
    this.tasks = [];
    this.loadRunningTasks();
  }

  private loadRunningTasks(): void {
    this.isLoading = true;
    this.isEmpty = false;

    this.subscriptionManager
      .createRequest(
        this.taskService.getRunningTasks(this.currentPage, this.pageSize),
        `runningTasks_${this.currentPage}`,
        this.instanceId
      )
      .subscribe({
        next: (response: TasksPaginatedResponse) => {
          this.tasks = (response.docs || []).sort(
            (a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()
          );
          this.hasNextPage = response.hasNextPage;
          this.hasPrevPage = response.hasPrevPage;
          this.isEmpty = this.tasks.length === 0;
          this.isLoading = false;
          this.runningTasksCount = response.totalDocs;
        },
        error: (error: any) => {
          this.errorService.handleError(error);
          this.isLoading = false;
          this.isEmpty = true;
        },
      });
  }

  private loadAllTasks(): void {
    if (this.currentPage === 1) {
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }

    this.subscriptionManager
      .createRequest(
        this.taskService.getAllTasks(this.currentPage, this.pageSize),
        `allTasks_${this.currentPage}`,
        this.instanceId
      )
      .subscribe({
        next: (response: TasksPaginatedResponse) => {
          const sorted = (response.docs || []).sort(
            (a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime()
          );
          if (this.currentPage === 1) {
            this.tasks = sorted;
          } else {
            this.tasks = [...this.tasks, ...sorted];
            this.isLoadingMore = false;
          }

          this.hasNextPage = response.hasNextPage;
          this.hasPrevPage = response.hasPrevPage;
          this.isEmpty = this.tasks.length === 0;
          this.isLoading = false;
        },
        error: (error: any) => {
          this.errorService.handleError(error);
          this.isLoading = false;
          this.isEmpty = this.currentPage === 1 && this.tasks.length === 0;
        },
      });
  }

  onScroll(event: any): void {
    if (!this.showHistory) {
      return;
    }

    const element = event.target;
    const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 10;

    if (atBottom && !this.isLoading && this.hasNextPage) {
      this.loadMoreTasks();
    }
  }

  private loadMoreTasks(): void {
    if (this.isLoading || !this.hasNextPage) {
      return;
    }

    this.currentPage++;
    this.loadAllTasks();
  }

  private scrollToTop(): void {
    if (this.tasksContainer) {
      try {
        this.tasksContainer.nativeElement.scrollTop = 0;
      } catch (err) {
        console.error('Error scrolling to top:', err);
      }
    }
  }

  getRelativeTime(date: Date): string {
    if (!date) {
      return '';
    }

    const now = new Date();
    const taskDate = new Date(date);
    const diffMs = now.getTime() - taskDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return "à l'instant";
    }
    if (diffMins < 60) {
      return `il y a ${diffMins} min`;
    }
    if (diffHours < 24) {
      return `il y a ${diffHours}h`;
    }
    return `il y a ${diffDays}j`;
  }

  retryTask(task: Task): void {
    this.subscriptionManager
      .createRequest(this.taskService.retryTask(task._id), `retryTask_${task._id}`, this.instanceId)
      .subscribe({
        error: (error: any) => this.errorService.handleError(error),
      });
  }

  dismissTask(task: Task): void {
    this.tasks = this.tasks.filter((t) => t._id !== task._id);
  }

  viewTaskResult(task: Task): void {
    let startDate = task.data.startDate;
    const campaign = task.data.campaign;
    if (startDate) {
      startDate = startDate.split('T')[0];
    }

    switch (task.operation) {
      case TaskOperations.ANESTH_NEEDS:
        this.navigateToFirstAccessiblePage([pages.parSallesBesoins], { date: startDate });
        break;
      case TaskOperations.CYCLE_PLANNING:
        this.navigateToFirstAccessiblePage([pages.hrSuiteListe], { date: startDate });
        break;
      case TaskOperations.TVO:
        this.navigateToFirstAccessiblePage([pages.parSalles, pages.hrsuiteSimpleDayNeeds], { date: startDate });
        break;
      case TaskOperations.STATS:
        this.handleStatTask(task);
        break;
      case TaskOperations.EXPORT_PROFILES_REPORTS:
        this.handleExportProfilesReportsTask(task);
        break;
      case TaskOperations.REQUEST_CALENDARS:
        this.navigateToFirstAccessiblePage([pages.hrSuiteListe], { date: startDate });
        break;
      case TaskOperations.ADJUSTMENT_PLANNING:
        this.navigateToFirstAccessiblePage([pages.hrSuiteListe], {
          openAdjustmentPopup: true,
          campaign,
        });
        break;
      case TaskOperations.CAMPAIGN_PLANNING:
        this.navigateToFirstAccessiblePage([pages.demandeRhCampaign], {
          openScenarioPopup: true,
          campaign,
        });
        break;
    }
  }

  private handleStatTask(task: Task) {
    if (task.data.excelExport) {
      this.subscriptionManager
        .createRequest(this.statopService.downloadExport(String(task._id)), 'downloadExport', this.instanceId)
        .subscribe({
          next: (response: HttpResponse<Blob>) => {
            if (response.body instanceof Blob) {
              const fineName = task.data.excelExport.fileName ?? 'export.xlsx';
              saveAs(response.body, fineName);
            }
          },
          error: (error: any) => this.errorService.handleError(error),
        });
    } else {
      this.navigateToFirstAccessiblePage([pages.statopBlocGeneral], {});
    }
  }

  private handleExportProfilesReportsTask(task: Task) {
    if (task.data?.zipExport?.blobUrl) {
      const fileName = task.data.zipExport.fileName ?? 'export-reports.zip';
      fetch(task.data.zipExport.blobUrl)
        .then((response) => response.blob())
        .then((blob) => saveAs(blob, fileName))
        .catch((error) => this.errorService.handleError(error));
    }
  }

  private navigateToFirstAccessiblePage(candidates: string[], queryParams?: any): void {
    const accessiblePage = candidates.find((page) => !!this.usersService.getAccessOptionsOfPage(page));

    if (accessiblePage) {
      this.navigationService.navigateTo(accessiblePage, queryParams, false);
    }
  }

  cancelTask(task: Task): void {
    this.subscriptionManager
      .createRequest(this.taskService.cancelTask(task._id), `cancelTask_${task._id}`, this.instanceId)
      .subscribe({
        next: (updatedTask: Task) => {
          const index = this.tasks.findIndex((t) => t._id === task._id);
          if (index !== -1) {
            this.tasks[index] = updatedTask;
          }
        },
        error: (error: any) => this.errorService.handleError(error),
      });
  }

  getTaskDuration(task: Task): string | null {
    if (!task.startedAt || !task.updatedAt) {
      return null;
    }
    return formatDuration(task.startedAt, task.updatedAt);
  }
}
